import React, { useState, useEffect } from 'react';
import { getData } from '../firebase';
import { invoiceSettlement } from '../ledgers';

const BRAND = '#0f6e56';

// type='purchase' = Payables, type='sales' = Receivables
export default function Payables({ user, mode, setPage }) {
  const isPayable = mode === 'payable';
  const invType = isPayable ? 'purchase' : 'sales';
  const accent = isPayable ? '#a32d2d' : '#0f6e56';
  const title = isPayable ? 'Payables' : 'Receivables';
  const subtitle = isPayable ? 'Money you owe to suppliers — settle via Payments' : 'Money owed to you by customers — settle via Receipts';

  const [invoices, setInvoices] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [inv, rec, pay] = await Promise.all([getData(user.uid, 'invoices'), getData(user.uid, 'receipts'), getData(user.uid, 'payments')]);
    setInvoices(inv); setReceipts(rec); setPayments(pay);
    setLoading(false);
  };

  useEffect(() => { load(); }, [mode]);

  const fmt = n => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const open = invoices
    .filter(i => i.type === invType)
    .map(i => ({ ...i, settle: invoiceSettlement(i, receipts, payments) }))
    .filter(i => i.settle.outstanding > 0.01);
  const total = open.reduce((s, i) => s + i.settle.outstanding, 0);
  const sorted = [...open].sort((a, b) => (a.dueDate || '').localeCompare(b.dueDate || ''));

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: '#aaa' }}>Loading...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700 }}>{title}</h2>
        <button onClick={() => setPage && setPage(isPayable ? 'payments' : 'receipts')} style={{ padding: '9px 18px', background: accent, color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          {isPayable ? '💸 Record Payment' : '💰 Record Receipt'}
        </button>
      </div>
      <div style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>{subtitle}</div>

      <div style={{ background: isPayable ? '#fcebeb' : '#e1f5ee', borderRadius: 14, padding: 24, marginBottom: 20, textAlign: 'center' }}>
        <div style={{ fontSize: 13, color: accent, marginBottom: 6 }}>Total {isPayable ? 'payable' : 'receivable'}</div>
        <div style={{ fontSize: 36, fontWeight: 700, color: accent }}>{fmt(total)}</div>
        <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>{open.length} open {open.length !== 1 ? 'items' : 'item'}</div>
      </div>

      {open.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, background: '#fff', borderRadius: 14, border: '1px solid #e8e8e8' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#0f6e56' }}>All settled!</div>
          <div style={{ fontSize: 13, color: '#aaa', marginTop: 4 }}>No outstanding {isPayable ? 'payables' : 'receivables'}</div>
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e8e8e8', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#f7f8fa' }}>
              {['Invoice', isPayable ? 'Supplier' : 'Customer', 'Date', 'Due Date', 'Invoice Amt', 'Outstanding'].map(h => (
                <th key={h} style={{ padding: '11px 16px', textAlign: h.includes('Amt') || h === 'Outstanding' ? 'right' : 'left', fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {sorted.map(inv => {
                const party = isPayable ? inv.supplierName : inv.customerName;
                const isOverdue = inv.dueDate && new Date(inv.dueDate) < new Date();
                return (
                  <tr key={inv.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                    <td style={td}><span style={{ fontSize: 12, color: '#888' }}>{inv.invoiceNo}</span></td>
                    <td style={{ ...td, fontWeight: 600 }}>{party}</td>
                    <td style={td}>{inv.date}</td>
                    <td style={td}>
                      {inv.dueDate || '—'}
                      {isOverdue && <span style={{ marginLeft: 8, fontSize: 10, background: '#fcebeb', color: '#a32d2d', padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>OVERDUE</span>}
                      {inv.settle.status === 'partial' && <span style={{ marginLeft: 8, fontSize: 10, background: '#e6f1fb', color: '#185fa5', padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>PARTIAL</span>}
                    </td>
                    <td style={{ ...td, textAlign: 'right', color: '#888' }}>{fmt(inv.amount)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: accent }}>{fmt(inv.settle.outstanding)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const td = { padding: '12px 16px', fontSize: 13, color: '#444' };
